import { Checkbox, Form, FormField, Modal } from 'semantic-ui-react';
import { Footer } from './Footer';
import {
  ErrorsContextProvider,
  RHFormDatePicker,
  RHFormNumberInput,
  RHFormSelect,
  RHFormTimePicker,
  ValidationErrors,
} from 'components';
import { useCreateLrwSession } from './useCreateLrwSession';
import { BaseSyntheticEvent } from 'react';
import { Controller } from 'react-hook-form';
import { OnComputerLabel, OnPaperLabel } from '../details/OnComputerWritingFormatsBadges';
import { dropdownListSearch } from 'utils';
import { lrwSessionFormStyles as S } from '../common/lrw-session-form';

export const CreateLrwSession = () => {
  const {
    form,
    generalErrors,
    venueOptions,
    isFetchingVenues,
    isFetchingDates,
    areVenueOptionsReady,
    areDateOptionsReady,
    handleSubmit,
    isCreating,
    created,
    minEndTime,
    isDateDisabled,
    venueSupportsOnPaper,
    venueSupportsOnComputer,
    availableVenues,
  } = useCreateLrwSession();

  const sessionEndTime = form.watch('sessionEndTime');

  return (
    <ErrorsContextProvider generalErrors={generalErrors}>
      <Modal
        open
        as={Form}
        onSubmit={(e: BaseSyntheticEvent) => {
          form.handleSubmit(handleSubmit)(e);
        }}
      >
        <Modal.Header>Create LRW session</Modal.Header>
        <Modal.Content>
          <ValidationErrors />
          <S.ModuleBadge />
          <RHFormDatePicker
            name="sessionDate"
            control={form.control}
            label="Date"
            disabledDates={isDateDisabled}
            disabled={!areDateOptionsReady}
            loading={isFetchingDates}
          />
          <S.TimePickersDiv>
            <RHFormTimePicker
              name="sessionStartTime"
              icon="clock outline"
              control={form.control}
              label="Start time"
              precision={30}
              onChange={(value) => {
                if (value && sessionEndTime && value >= sessionEndTime) {
                  form.resetField('sessionEndTime');
                }
              }}
              data-testid="session-start-time"
            />
            <RHFormTimePicker
              name="sessionEndTime"
              icon="clock outline"
              control={form.control}
              label="End time"
              precision={30}
              min={minEndTime}
              max="24:00"
              extraValues={[
                {
                  hours: 24,
                  minutes: 0,
                },
              ]}
              data-testid="session-end-time"
            />
          </S.TimePickersDiv>
          <RHFormSelect
            name="venueId"
            control={form.control}
            label="Venue"
            options={venueOptions}
            disabled={!areVenueOptionsReady}
            loading={isFetchingVenues}
            search={dropdownListSearch}
            openOnFocus
            minCharacters={0}
            onChange={(venueId) => {
              const venue = availableVenues?.find((x) => x.id === venueId);
              form.setValue('onComputerEnabled', venue?.supportsOnComputer ?? false);
              form.setValue('onPaperEnabled', venue?.supportsOnPaper ?? false);
            }}
          />
          <FormField>
            <S.FieldGroupLabel id="writing-format-group">Writing format</S.FieldGroupLabel>
            <S.CheckboxWithCustomLabel>
              <Controller
                name="onComputerEnabled"
                control={form.control}
                render={({ field }) => (
                  <Checkbox
                    id="lrw-session-on-computer"
                    disabled={!venueSupportsOnComputer}
                    checked={field.value}
                    onChange={(_, data) => field.onChange(data.checked)}
                    aria-labelledby="writing-format-group"
                  />
                )}
              />
              <label htmlFor="lrw-session-on-computer">
                <OnComputerLabel>CD</OnComputerLabel>
              </label>
            </S.CheckboxWithCustomLabel>
            <S.CheckboxWithCustomLabel>
              <Controller
                name="onPaperEnabled"
                control={form.control}
                render={({ field }) => (
                  <Checkbox
                    id="lrw-session-on-paper"
                    disabled={!venueSupportsOnPaper}
                    checked={field.value}
                    onChange={(_, data) => field.onChange(data.checked)}
                    aria-labelledby="writing-format-group"
                  />
                )}
              />
              <label htmlFor="lrw-session-on-paper">
                <OnPaperLabel>+P</OnPaperLabel>
              </label>
            </S.CheckboxWithCustomLabel>
          </FormField>
          <RHFormNumberInput
            label="Test taker admission time"
            name="testTakerAdmissionTimeMinutes"
            control={form.control}
            min={0}
            max={99}
            innerLabel={{ basic: true, content: 'min' }}
            innerLabelPosition="right"
          />
        </Modal.Content>
        <Footer created={created} isCreating={isCreating} />
      </Modal>
    </ErrorsContextProvider>
  );
};
